import * as exceptions from '#common/exceptions/authExceptions'
import ga4 from '#common/utils/ga4'
import { identifyUser } from '#common/utils/user'

const EMPTY_USER = {
  first_name: '',
  last_name: '',
  is_authenticated: false,
  analytics_id: null,
  partner_id: 23,
  state: undefined,
  zipcode: undefined,
  status: undefined,
  memberships: [],
  braintree_token: undefined,
  notifyme_products: {},
  notifyme_wineries: {},
  push_notifications: [],
  sessionid: undefined,
}

export const useAuth = defineStore('auth', () => {
  const { $store, $cookies, $axios } = useNuxtApp()
  const requestFetch = useRequestFetch()
  const metadata = useMetadata()
  const paymentMethods = usePaymentMethods()
  const router = useRouter()
  const userListStore = useUserList()

  // from login.js
  const authModalOpen = ref(false)
  const authModalTheme = ref({})
  const authStep = ref('email-check')
  const authEmail = ref(null)
  const authZipcode = ref(null)
  const authRedirect = ref(null)
  const authIsClub = ref(false)
  const qes = ref(false)
  const checkUserExistsError = ref(null)

  const userLoaded = ref(false)
  const user = ref({ ...EMPTY_USER })
  const userRegisteredFromLoginModal = ref(false)
  const userLoggedInFromLoginModal = ref(false)
  const postLoginActionPending = ref(false)

  const braintreeStore = useBraintree()

  const loggedIn = computed(() => user?.value?.is_authenticated)
  const possessiveTitleUserName = computed(() => {
    const firstName = user?.value?.first_name
    if (!firstName) return "User's"

    return `${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}${
      firstName.endsWith('s') ? "'" : "'s"
    }`
  })

  const openAuthModal = ({
    theme,
    step,
    email,
    zipcode,
    redirect,
    isClub,
  } = {}) => {
    authModalOpen.value = true
    if (theme !== undefined) authModalTheme.value = theme
    if (step !== undefined) authStep.value = step
    if (email !== undefined) authEmail.value = email
    if (zipcode !== undefined) authZipcode.value = zipcode
    if (isClub !== undefined) authIsClub.value = isClub
    authRedirect.value =
      redirect !== undefined ? redirect : router.currentRoute.value.fullPath
  }

  const closeAuthModal = () => {
    authModalOpen.value = false
    checkUserExistsError.value = false
  }

  const resetAuthModal = () => {
    closeAuthModal()
    authModalTheme.value = {}
    authStep.value = 'email-check'
    authEmail.value = null
    authZipcode.value = null
    authRedirect.value = null
    authIsClub.value = false
    checkUserExistsError.value = false
  }

  const setAuthStep = (step) => {
    authStep.value = step
  }

  const requireLogin = async ({ redirect, isClub = false } = {}) => {
    if (loggedIn.value) return true

    openAuthModal({ redirect, isClub })

    // Redirect to home if user closes modal (without logging in)
    // OR if user logs out while on this page
    return await new Promise((resolve) => {
      const stop = watch(
        () => [authModalOpen.value, loggedIn.value],
        ([authModalOpenNew, loggedInNew]) => {
          if (!authModalOpenNew && !loggedInNew) {
            stop()
            navigateTo('/')
            resolve(loggedIn.value)
          }
        },
      )
    })
  }

  const setUser = (newUser) => {
    user.value = newUser
    userLoaded.value = true
    if (user.value?.is_authenticated) {
      identifyUser({ user: user.value }, $cookies)
    }
  }

  const checkUserExists = async (email) => {
    checkUserExistsError.value = null
    try {
      const response = await requestFetch('/api/customer/check_email/', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })

      if (response?.non_field_errors?.length) {
        checkUserExistsError.value = response.non_field_errors
        return
      }

      authEmail.value = email
      authZipcode.value = response.zip_code || null
      setAuthStep(response.user ? 'login' : 'register')
    } catch (err) {
      if (err.data?.non_field_errors?.length) {
        checkUserExistsError.value = err.data.non_field_errors
        return
      }
      checkUserExistsError.value = [
        'Error: Something went wrong. Please try again.',
      ]
      console.error('checkUserExists failed:', err)
    }
  }

  const loadUser = async (force = false) => {
    if (!force && userLoaded.value) return
    userLoaded.value = true
    const data = await requestFetch(`/api/customer/user/`)
    setUser(data)
  }

  const handleAuth = async (config) => {
    // this is used by login, register, change_password and logout
    // feel free to add any tidy-up logic related to a change in user here
    let response = { data: {} }
    const isLogout = config.url === '/api/customer/logout/'
    try {
      response = await $axios.request(config)
    } catch (e) {
      response = e.response
      response.isError = true
    }
    if (response.isError) {
      console.log('response', response)
      if (response.status === 429) {
        throw new exceptions.RateLimitedException(
          'Too many attempts. Please try again later.',
        )
      }
      throw new exceptions.AuthHandlerException(response)
    }
    braintreeStore.resetBraintree()
    if (authRedirect.value) navigateTo(authRedirect.value)

    nextTick(async () => {
      await loadUser(true)
      metadata.loadAll(true)
      $store.dispatch('getCart')
      $store.dispatch('setNotifications', [])
      $store.dispatch('getPromoBanner')
      paymentMethods.clearPaymentMethods()
      if (!isLogout) {
        $store.dispatch('fetchPPTOEligibleOrder')
        userListStore.loadAllLists(true)
      } else {
        userListStore.resetLists()
      }
      resetAuthModal()
    })
    return response
  }

  const registerUser = async (data) => {
    const response = await handleAuth({
      url: '/api/customer/register/',
      method: 'POST',
      data: {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        default_zipcode: data.default_zipcode,
        date_of_birth: data.date_of_birth,
        password: data.password,
        is_club: data.loginIsClub || false,
        recaptcha: data.recaptcha,

        receive_shipping_updates_by_sms: data.receive_shipping_updates_by_sms,
        sms_number: data?.sms_number || '',
        email_subscription_status: data.email_subscription_status,
        cold_pack_option: data.cold_pack_option,
        allow_ice_pack: data.allow_ice_pack,
        hold_until_fall: data.hold_until_fall,
        address: data.address,
      },
    })
    ga4.track('register')
    return response.data
  }

  const loginUser = async (params) => {
    const response = await handleAuth({
      url: '/api/customer/login/',
      method: 'POST',
      data: {
        email: params.email,
        password: params.password,
        path: params?.path ?? route.path,
        recaptcha: params?.recaptcha,
      },
    })
    ga4.track('login')
    userLoggedInFromLoginModal.value = !!params.loggedInFromLoginModal
    return response?.data
  }

  const changePasswordForUser = async (body) => {
    await handleAuth({
      url: '/api/customer/change_password/',
      method: 'POST',
      data: body,
    })
    ga4.track('change_password')
  }

  const logoutUser = async () => {
    user.value = { ...EMPTY_USER }
    userLoggedInFromLoginModal.value = false
    userRegisteredFromLoginModal.value = false
    await handleAuth({ url: '/api/customer/logout/' })
    ga4.track('logout')
    navigateTo('/')
  }

  const registerQes = async (data) => {
    await requestFetch(`/api/customer/qes/`, {
      method: 'POST',
      body: JSON.stringify({
        email: data.qesEmail,
        zip: data.zipcode,
        daily_email_active: true,
      }),
    })
    qes.value = true
  }

  const resetQes = () => {
    qes.value = false
  }

  return {
    authModalOpen,
    authModalTheme,
    authStep,
    authEmail,
    authZipcode,
    authRedirect,
    authIsClub,
    openAuthModal,
    closeAuthModal,
    setAuthStep,
    checkUserExists,
    checkUserExistsError,
    qes,
    registerQes,
    resetQes,

    userLoaded,
    user,
    loggedIn,
    userRegisteredFromLoginModal,
    userLoggedInFromLoginModal,
    possessiveTitleUserName,
    setUser,
    loadUser,
    registerUser,
    changePasswordForUser,
    loginUser,
    logoutUser,
    requireLogin,
    resetAuthModal,
    postLoginActionPending,
  }
})
